'use client'

import Link from 'next/link'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  BinaryIcon,
  DownloadIcon,
  EditIcon,
  FolderGitIcon,
  HardDriveIcon,
  LockIcon,
  Trash2Icon,
  CopyIcon
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ContentDetailInfo, ContentFileIcon, DeleteConfirmationDialog, LoadingState } from '@/components/partials'
import { useFile, useDeleteFile, useDownloadFile, useCopyToClipboard } from '@/hooks'
import { formatFileSize, getResizedPictureUrl } from '@/lib/utils'
import { useState } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'

type PropsType = {
  id: string
}

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [selectedDownloadId, setSelectedDownloadId] = useState<string | null>(null)

  const { data: file, isLoading, error, refetch } = useFile(id)
  const { mutate: deleteFileMutate, isPending: isDeleting } = useDeleteFile()
  const { mutate: downloadFileMutate, isPending: isDownloading, cancelDownload } = useDownloadFile()

  const isImage = file?.type.startsWith('image/')

  const handleDelete = () => {
    setShowDeleteConfirmation(true)
  }

  const confirmDelete = () => {
    deleteFileMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['files'] })
        toast.success(t('deleted_successfully'))
        setShowDeleteConfirmation(false)
        router.push('/files')
      },
      onError: error => {
        console.error('Error deleting file:', error)
        toast.error(t('error_deleting'))
        setShowDeleteConfirmation(false)
      }
    })
  }

  const handleDownload = () => {
    if (!file) {
      return
    }

    downloadFileMutate(file.id, {
      onSuccess: () => {
        // Success is handled in the store
      },
      onError: error => {
        console.error('Error downloading file:', error)

        // Don't show error toast if it's a cancellation error
        const errorMessage = error?.message || String(error)
        const isCancellationError =
          errorMessage.toLowerCase().includes('canceled') ||
          errorMessage.toLowerCase().includes('canceled') ||
          errorMessage.toLowerCase().includes('abort')

        if (!isCancellationError) {
          toast.error(t('error_downloading_file'))
        }
      }
    })
  }

  const confirmCancelDownload = () => {
    if (selectedDownloadId) {
      cancelDownload(selectedDownloadId)
      setShowCancelConfirmation(false)
      setSelectedDownloadId(null)
      toast.success(t('download_canceled'))
    }
  }

  return (
    <>
      <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch}>
        {file && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 items-center justify-between gap-2 sm:grid-cols-2">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" asChild>
                  <Link href="/files">
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span className="hidden lg:flex">{t('list')}</span>
                  </Link>
                </Button>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-4 shrink-0">
                      <ContentFileIcon file={file} />
                    </div>
                    <h1 className="text-2xl font-bold">{file.name}</h1>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-muted-foreground text-sm">{file.id}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(file.id, t('copied'))}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex flex-row items-center justify-end gap-2">
                <Button variant="outline" asChild>
                  <Link href={`/files/${id}/edit`}>
                    <EditIcon className="h-4 w-4" />
                    <span className="hidden lg:flex">{t('edit_file')}</span>
                  </Link>
                </Button>
                <Button variant="outline" onClick={handleDownload} disabled={isDownloading}>
                  <DownloadIcon className="h-4 w-4" />
                  <span className="hidden lg:flex">{t('download')}</span>
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
                </Button>
              </div>
            </div>

            <Card>
              <CardContent>
                <div className="space-y-6">
                  {isImage && (
                    <div className="mb-6">
                      <h3 className="mb-4">{t('preview')}</h3>
                      <div className="flex justify-start">
                        <PhotoProvider maskOpacity={0.5}>
                          <PhotoView src={getResizedPictureUrl(file.path, 800, 600)}>
                            <div className="bg-checkerboard inline-flex items-center justify-center rounded-md border p-0.5">
                              <Image
                                unoptimized
                                src={getResizedPictureUrl(file.path, 240, 240)}
                                alt={file.name}
                                width={240}
                                height={240}
                                className={`max-h-100 w-auto cursor-pointer rounded-md object-contain hover:opacity-90 ${file.type === 'image/svg+xml' || file.path.toLowerCase().endsWith('.svg') ? 'max-w-60' : ''}`}
                              />
                            </div>
                          </PhotoView>
                        </PhotoProvider>
                      </div>
                      <Separator className="my-6" />
                    </div>
                  )}

                  <div className="flex flex-col space-y-5">
                    <div>
                      <h3 className="mb-4">{t('information')}</h3>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-1">
                          <p className="text-muted-foreground">{t('name')}</p>
                          <p>{file.name}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground text-sm">{t('type')}</p>
                          <div className="flex items-center">
                            <div className="mr-1 h-5 w-4 shrink-0">
                              <ContentFileIcon file={file} />
                            </div>
                            <span>{file.type}</span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground">{t('path')}</p>
                          <p className="flex items-center">
                            <FolderGitIcon className="mr-2 h-4 w-4" />
                            {file.path}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground">{t('size')}</p>
                          <p className="flex items-center">
                            <BinaryIcon className="mr-2 h-4 w-4" />
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground">{t('driver')}</p>
                          <p className="flex items-center">
                            <HardDriveIcon className="mr-2 h-4 w-4" />
                            {t(file.driver)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-muted-foreground">{t('access_level')}</p>
                          <p className="flex items-center">
                            <LockIcon className="mr-2 h-4 w-4" />
                            {t(file.accessLevel)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Separator />
                    <ContentDetailInfo entity={file} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </LoadingState>

      <DeleteConfirmationDialog
        open={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={showCancelConfirmation}
        onCancel={() => {
          setShowCancelConfirmation(false)
          setSelectedDownloadId(null)
        }}
        onConfirm={confirmCancelDownload}
        title={t('cancel_download')}
        description={t('cancel_download_confirmation')}
      />
    </>
  )
}

export default Detail
